import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { cloneListOfCloneable, isSameData, isSameList } from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { ArtistProfile } from './artist-profile.js';
import { EventInfo } from './event-info.js';

export class ArtistPageInfo extends DataModel {
  static readonly TYPE_ID: string = '51047fa8dc754a84bbe996f19222a54f';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly artist: ArtistProfile,
    public readonly events: EventInfo[],
    public readonly metaPixelId: string | null,
    public readonly tikTokPixelId: string | null,
    public readonly googleTagManagerId: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return ArtistPageInfo.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ArtistPageInfo.SCHEMA_VERSION);
  }

  static create(args: {
    artist: ArtistProfile,
    events: EventInfo[],
    metaPixelId: string | null,
    tikTokPixelId: string | null,
    googleTagManagerId: string | null,
  }): ArtistPageInfo {
    return new ArtistPageInfo(
        args.artist,
        args.events,
        args.metaPixelId,
        args.tikTokPixelId,
        args.googleTagManagerId,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ArtistPageInfo {
    return ArtistPageInfo.create({
      artist: context.fromData(JSONUtils.decodeRecord(MapUtils.get<unknown>(map, 'artist'))),
      events: MapUtils.getList<EventInfo>(
          map,
          'events',
          (data) => EventInfo.fromMap(context, JSONUtils.decodeRecord(data)),
      ),
      metaPixelId: MapUtils.getStringOrNull(map, 'metaPixelId'),
      tikTokPixelId: MapUtils.getStringOrNull(map, 'tikTokPixelId'),
      googleTagManagerId: MapUtils.getStringOrNull(map, 'googleTagManagerId'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      artist: context.toData(this.artist),
      events: this.events.map((event) => event.toMap(context)),
      metaPixelId: this.metaPixelId,
      tikTokPixelId: this.tikTokPixelId,
      googleTagManagerId: this.googleTagManagerId,
    };
  }

  clone(): this {
    return ArtistPageInfo.create({
      artist: this.artist.clone(),
      events: cloneListOfCloneable(this.events),
      metaPixelId: this.metaPixelId,
      tikTokPixelId: this.tikTokPixelId,
      googleTagManagerId: this.googleTagManagerId,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.artist, this.artist) &&
        isSameList(other.events, this.events, (a, b) => isSameData(a, b)) &&
        other.metaPixelId === this.metaPixelId &&
        other.tikTokPixelId === this.tikTokPixelId &&
        other.googleTagManagerId === this.googleTagManagerId;
  }
}

export class ArtistPageInfoBuilder extends DataModelBuilder<ArtistPageInfo> {
  build(context: SerializationContext, map: SerializedData): ArtistPageInfo {
    return ArtistPageInfo.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ArtistPageInfo.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ArtistPageInfo.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {version = 2;}

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
